import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

/**
 * Multi-selección de la INTERVENCIÓN de quirófano desde el catálogo de
 * cirugías (/api/surgeries). Las cirugías reales son combinadas ("Blefaro +
 * lipofilling + CO2"), así que se pueden marcar varias; el valor es un string
 * con los nombres unidos por " + " (compatible con la auto-detección de
 * consentimientos por palabras clave del backend).
 *
 * Props: value (string), onChange(string), testId.
 */
export default function IntervencionSelect({ value, onChange, testId }) {
  const [surgeries, setSurgeries] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get('/api/surgeries')
      .then((r) => setSurgeries((Array.isArray(r.data) ? r.data : []).filter((s) => s.active !== false)))
      .catch(() => {});
  }, []);

  const selected = useMemo(
    () => (value || '').split(' + ').map((s) => s.trim()).filter(Boolean),
    [value],
  );

  const toggle = (name) => {
    const set = new Set(selected);
    if (set.has(name)) set.delete(name); else set.add(name);
    // Orden estable = orden del catálogo; conserva al final los nombres que no
    // estén en el catálogo (por si venían de una cita antigua tecleada a mano).
    const ordered = surgeries.filter((s) => set.has(s.name)).map((s) => s.name);
    const extras = selected.filter((x) => set.has(x) && !surgeries.some((s) => s.name === x));
    onChange([...ordered, ...extras].join(' + '));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          className="w-full justify-between font-normal mt-1.5 h-auto min-h-9 py-2"
          data-testid={testId}
        >
          <span className={`text-left whitespace-normal ${!value ? 'text-muted-foreground' : ''}`}>
            {value || 'Elegir intervención…'}
          </span>
          <ChevronDown className="w-4 h-4 ml-2 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-1 max-h-72 overflow-y-auto" align="start">
        {surgeries.length === 0 ? (
          <div className="px-2 py-3 text-sm text-muted-foreground text-center">Cargando cirugías…</div>
        ) : (
          surgeries.map((s) => {
            const on = selected.includes(s.name);
            return (
              <button
                key={s.code || s.id || s.name}
                type="button"
                onClick={() => toggle(s.name)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left hover:bg-[rgba(46,110,94,0.08)]"
                data-testid={`intervencion-opt-${s.code || s.name}`}
              >
                <Check className={`w-4 h-4 shrink-0 ${on ? 'opacity-100 text-[#2E6E5E]' : 'opacity-0'}`} />
                <span className="flex-1">{s.name}</span>
              </button>
            );
          })
        )}
      </PopoverContent>
    </Popover>
  );
}
